import AddIcon from '@material-design-icons/svg/filled/add.svg?react';
import TicketIcon from '@material-design-icons/svg/filled/confirmation_number.svg?react';
import ContentCopyIcon from '@material-design-icons/svg/filled/content_copy.svg?react';
import DeleteIcon from '@material-design-icons/svg/filled/delete.svg?react';
import { isLeft, isRight } from 'fp-ts/Either';
import { use, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { TRPCContext } from '../contexts/TRPC';
import { useAsyncData } from '../hooks/useAsyncData';
import { captureAndReportError } from '../utils/errorReporting';
import { DataTable } from './common/DataTable';
import { Button } from './ui/button';

interface InviteCodeDetail {
  id: string;
  code: string;
  status: 'available' | 'redeemed';
  redeemedAt: number | null;
  redeemedBy: {
    email: string;
    fullName: string;
  } | null;
  created: number;
}

export function InviteCodePanel() {
  const [filterStatus, setFilterStatus] = useState<
    'all' | 'available' | 'redeemed'
  >('all');
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const {
    data: inviteCodes,
    isLoading,
    run,
  } = useAsyncData<InviteCodeDetail[]>({
    initialData: [],
    initialLoading: true,
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const trpc = use(TRPCContext);

  const loadInviteCodes = async () => {
    await run(
      () =>
        trpc.client.listInviteCodes.query({
          status: filterStatus,
        }),
      {
        onSuccess: (result, helpers) => {
          if (isRight(result)) {
            helpers.setData(result.right.inviteCodes);
          } else if (isLeft(result)) {
            toast.error(result.left.message);
          }
        },
        onError: (error) => {
          captureAndReportError(error, {
            operation: 'admin.loadInviteCodes',
            component: 'InviteCodePanel',
            filterStatus,
          });
          toast.error('Failed to load invite codes');
        },
      },
    );
  };

  useEffect(() => {
    loadInviteCodes();
  }, [filterStatus]);

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      const result = await trpc.client.generateInviteCode.mutate({});
      if (isRight(result)) {
        setGeneratedCode(result.right.code);
        toast.success('Invite code generated successfully');
        loadInviteCodes();

        navigator.clipboard.writeText(result.right.code).then(() => {
          toast.success('Code copied to clipboard');
        });
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.generateInviteCode',
        component: 'InviteCodePanel',
      });
      toast.error('Failed to generate invite code');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setDeletingId(id);
      const result = await trpc.client.deleteInviteCode.mutate({ id });
      if (isRight(result)) {
        toast.success('Invite code deleted');
        loadInviteCodes();
      } else if (isLeft(result)) {
        toast.error(result.left.message);
      }
    } catch (error) {
      captureAndReportError(error, {
        operation: 'admin.deleteInviteCode',
        component: 'InviteCodePanel',
        inviteCodeId: id,
      });
      toast.error('Failed to delete invite code');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString();
  };

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success('Code copied to clipboard');
    });
  };

  const columns = [
    {
      key: 'code',
      header: 'Code',
      headerClassName: 'pb-3 text-sm font-medium text-neutral-400',
      className: 'py-4',
      accessor: (code: InviteCodeDetail) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-white">{code.code}</span>
          <button
            type="button"
            onClick={() => handleCopy(code.code)}
            className="text-neutral-400 hover:text-white transition-colors"
            title="Copy to clipboard"
          >
            <ContentCopyIcon className="fill-current size-4" />
          </button>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      headerClassName: 'pb-3 text-sm font-medium text-neutral-400',
      className: 'py-4',
      accessor: (code: InviteCodeDetail) => (
        <span
          className={`inline-flex px-2 py-1 text-xs rounded-full ${
            code.status === 'available'
              ? 'bg-green-600/20 text-green-400 border border-green-600/30'
              : 'bg-neutral-600/20 text-neutral-400 border border-neutral-600/30'
          }`}
        >
          {code.status}
        </span>
      ),
    },
    {
      key: 'redeemedBy',
      header: 'Redeemed By',
      headerClassName: 'pb-3 text-sm font-medium text-neutral-400',
      className: 'py-4 text-sm text-neutral-300',
      accessor: (code: InviteCodeDetail) =>
        code.redeemedBy ? (
          <div>
            <div>{code.redeemedBy.fullName}</div>
            <div className="text-xs text-neutral-400">
              {code.redeemedBy.email}
            </div>
          </div>
        ) : (
          <span className="text-neutral-500">-</span>
        ),
    },
    {
      key: 'redeemedAt',
      header: 'Redeemed At',
      headerClassName: 'pb-3 text-sm font-medium text-neutral-400',
      className: 'py-4 text-sm text-neutral-400',
      accessor: (code: InviteCodeDetail) => formatDate(code.redeemedAt),
    },
    {
      key: 'created',
      header: 'Created',
      headerClassName: 'pb-3 text-sm font-medium text-neutral-400',
      className: 'py-4 text-sm text-neutral-400',
      accessor: (code: InviteCodeDetail) => formatDate(code.created),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'pb-3 text-sm font-medium text-neutral-400',
      className: 'py-4',
      accessor: (code: InviteCodeDetail) =>
        code.status === 'available' ? (
          <button
            type="button"
            onClick={() => handleDelete(code.id)}
            className="text-red-400 hover:text-red-300 transition-colors"
            title="Delete code"
            disabled={deletingId === code.id}
          >
            <DeleteIcon className="fill-current size-4" />
          </button>
        ) : (
          <span className="text-neutral-500">-</span>
        ),
    },
  ];

  return (
    <div className="md:bg-neutral-800/50 md:rounded-2xl md:border md:border-neutral-700 overflow-hidden">
      <div className="px-6 py-5 border-b border-neutral-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <TicketIcon className="fill-current text-white size-5" />
              <h3 className="text-lg font-semibold text-white">
                Invite Code Management
              </h3>
            </div>
            <p className="mt-2 text-sm text-neutral-400">
              Generate and manage invite codes for user registration.
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={handleGenerate}
            disabled={isGenerating}
            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white w-full md:w-auto justify-center"
          >
            <AddIcon className="fill-current size-4" />
            {isGenerating ? 'Generating...' : 'Generate Code'}
          </Button>
        </div>
      </div>

      {generatedCode && (
        <div className="px-6 py-4 bg-teal-600/10 border-b border-neutral-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-400 mb-1">
                New invite code generated:
              </p>
              <p className="text-xl font-mono text-teal-400">{generatedCode}</p>
            </div>
            <button
              type="button"
              onClick={() => setGeneratedCode(null)}
              className="text-neutral-400 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="px-6 py-4 border-b border-neutral-700 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          <span className="text-sm text-neutral-400">Filter:</span>
          {(['all', 'available', 'redeemed'] as const).map((status) => (
            <button
              type="button"
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                filterStatus === status
                  ? 'bg-teal-600/20 text-teal-400 border border-teal-600/30'
                  : 'bg-neutral-900 text-neutral-400 border border-neutral-700 hover:border-neutral-600'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="md:p-6">
        <DataTable
          data={inviteCodes}
          columns={columns}
          isLoading={isLoading}
          emptyMessage="No invite codes found"
          renderMobileItem={(code) => (
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-white font-medium text-lg">
                    {code.code}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(code.code)}
                    className="text-neutral-400 hover:text-white transition-colors"
                    title="Copy to clipboard"
                  >
                    <ContentCopyIcon className="fill-current size-4" />
                  </button>
                </div>
                <span
                  className={`inline-flex px-2 py-1 text-xs rounded-full ${
                    code.status === 'available'
                      ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                      : 'bg-neutral-600/20 text-neutral-400 border border-neutral-600/30'
                  }`}
                >
                  {code.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs text-neutral-400">
                <div>
                  <span className="text-neutral-500">Created:</span>{' '}
                  <span className="text-neutral-300">
                    {formatDate(code.created)}
                  </span>
                </div>
                {code.redeemedAt && (
                  <div>
                    <span className="text-neutral-500">Redeemed:</span>{' '}
                    <span className="text-neutral-300">
                      {formatDate(code.redeemedAt)}
                    </span>
                  </div>
                )}
              </div>

              {code.redeemedBy && (
                <div className="text-xs text-neutral-400 border-t border-neutral-700/50 pt-2">
                  <div className="text-neutral-500 mb-0.5">Redeemed By:</div>
                  <div className="text-neutral-300 font-medium">
                    {code.redeemedBy.fullName}
                  </div>
                  <div className="text-neutral-500">
                    {code.redeemedBy.email}
                  </div>
                </div>
              )}

              {code.status === 'available' && (
                <div className="pt-2 border-t border-neutral-700/50 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleDelete(code.id)}
                    className="text-red-400 hover:text-red-300 transition-colors flex items-center gap-1 text-xs font-medium"
                    title="Delete code"
                    disabled={deletingId === code.id}
                  >
                    <DeleteIcon className="fill-current size-4" />
                    Delete Code
                  </button>
                </div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
}
